import { Diff } from "../../taskpane/features/generate/elements/MyCarousel";
import { Question } from "../../type";
import { getQuestionIds } from "./excelInfo";
import { get } from "lodash";
import { importQuestion } from "./import";
import { cordinateSpace, removeSpace } from "./utils";

// export function compareQuestions(cur: Question[], prev: Question[]) {
//   // 設問IDの並び順を比較
//   const curIds = cur.map((q) => q.questionId);
//   const prevIds = prev.map((q) => q.questionId);

//   if (cur.length < prev.length) {
//     return null;
//   } else if (cur.length > prev.length) {
//     return {
//       questionId: curIds.filter((id) => !prevIds.includes(id))[0],
//     };
//   }

//   // 並び順が同じかどうかを比較
//   if (curIds.join(",") != prevIds.join(",")) {
//     const temp = curIds.map((id, index) => {
//       if (id !== prevIds[index]) {
//         return id;
//       } else {
//         return null;
//       }
//     });

//     return {
//       questionId: temp.filter((id) => id !== null)[0],
//     };
//   } else {
//     for (let i = 0; i < cur.length; i++) {
//       if (cur[i].questionId !== prev[i].questionId) {
//         return {
//           questionId: cur[i].questionId,
//         };
//       }
//     }
//     return null;
//   }
// }

export function compareObjects(
  obj1: Question,
  obj2: Question
): { stringDiffs: string[]; arrayDiffs: string[] } {
  const stringDiffs: string[] = [];
  const arrayDiffs: string[] = [];

  // 全Keyを列挙（obj1とobj2の和集合）
  const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

  allKeys.forEach((key) => {
    const val1 = obj1[key];
    const val2 = obj2[key];

    // 文字列型の比較
    if (typeof val1 === "string" && typeof val2 === "string") {
      if (cordinateSpace(val1) !== cordinateSpace(val2)) {
        stringDiffs.push(key);
      }
    }

    // 配列型の比較
    else if (Array.isArray(val1) && Array.isArray(val2)) {
      if (!arraysEqual(val1, val2)) {
        arrayDiffs.push(key);
      }
    }
  });

  return { stringDiffs, arrayDiffs };
}

// 配列の完全一致を確認する関数
function arraysEqual(arr1: any[], arr2: any[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (!deepEqual(arr1[i], arr2[i])) {
      return false;
    }
  }

  return true;
}

// オブジェクトのdeepEqual比較
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (typeof a !== "object" || a === null || b === null) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (const key of keysA) {
    if (!deepEqual(a[key], b[key])) {
      return false;
    }
  }

  return true;
}

export async function checkDiffAddinExcel(questions: Question[], editSheet: string) {
  // AddinとExcelの質問IDを取得する
  const { addin, excel } = await getQuestionIds(questions, editSheet);

  // AddinとExcelの質問が一致しているかを確認する
  const isEqual = addin.length === excel.length && addin.every((id, idx) => id === excel[idx]);

  if (isEqual) {
    return {
      onlyInAddin: [],
      onlyInExcel: [],
      orderMap: [],
      moveStep: [],
      addin,
      excel,
    };
  }

  // もし一致していない場合...
  // Addinにはあるが、Excelにはない場合
  const onlyInAddin = addin.filter((id) => !excel.includes(id));

  // if (onlyInAddin.length) {
  //   // insertQuestion()
  // }

  // Excelにはあるが、Addinにはない
  const onlyInExcel = excel.filter((id) => !addin.includes(id));

  // 順番が異なる
  const orderMap: number[][] = [];
  // addin.forEach((id, index) => {
  //   const indexInExcel = excel.indexOf(id);
  //   orderMap[index] = [index, indexInExcel == -1 ? undefined : indexInExcel];
  // });

  excel.forEach((id, index) => {
    const indexInAddin = addin.indexOf(id);
    if (indexInAddin !== -1) {
      orderMap[index] = [index, indexInAddin];
    }
  });

  const moveStep = onlyInAddin.length || onlyInExcel.length ? [] : getMoveSteps(excel, addin);

  return {
    onlyInAddin,
    onlyInExcel,
    orderMap,
    addin,
    excel,
    moveStep,
  };
}

function getMoveSteps<T>(source: T[], target: T[]): [number, number][] {
  if (source.length !== target.length) {
    throw new Error("source と target の長さが一致しません");
  }

  const steps: [number, number][] = [];
  const current = [...source];

  for (let i = 0; i < target.length; i++) {
    if (current[i] === target[i]) continue;

    // target[i] を current から探す
    const fromIndex = current.findIndex((v) => v === target[i]);
    if (fromIndex === -1) {
      throw new Error(`target の要素 "${target[i]}" が source に存在しません`);
    }

    // 要素を移動（spliceしてinsert）
    const [item] = current.splice(fromIndex, 1);
    current.splice(i, 0, item);

    // 操作記録
    steps.push([fromIndex, i]);
  }

  return steps;
}

export async function compareQuestionsValue(editSheet: string, questions: Question[]) {
  const { addin } = await getQuestionIds(questions, editSheet);
  const diffs: Diff[] = [];

  for (const qId of addin) {
    const selectedIndex = questions.findIndex((q) => q.questionId === qId);
    const nextQuestionId = get(questions[selectedIndex + 1], "questionId", undefined) || "";

    const lastQuestionIdInAddin = addin[addin.length - 1];

    const questionImported = await importQuestion(
      editSheet,
      qId,
      qId == lastQuestionIdInAddin ? undefined : nextQuestionId
    );

    const contentDiff = compareObjects(questions[selectedIndex], questionImported);

    [...contentDiff.stringDiffs, ...contentDiff.arrayDiffs].forEach((diff) => {
      const addInValue = get(questions[selectedIndex], diff, "");
      const addInText =
        typeof addInValue === "string"
          ? addInValue
          : addInValue.map((i) => (diff == "optionHeaders" ? i.label : i.text)).join(",");

      const excelValue = get(questionImported, diff, "");
      const excelText =
        typeof excelValue === "string"
          ? excelValue
          : excelValue.map((i) => (diff == "optionHeaders" ? i.label : i.text)).join(",");

      diffs.push({
        questionId: qId,
        diff: diff,
        excel: excelText,
        addIn: addInText,
      });
    });
  }
  return diffs;
}
